import "./styles.css";
import { createRoot } from "react-dom/client";
import App from "./app";
import { Providers } from "@/providers";
import React from "react";
import LoginScreen from "./components/login/LoginScreen";
import { useAuth0 } from "@auth0/auth0-react";
import { ErrorBoundary } from "./components/error-boundary/ErrorBoundary";
import { useRef, useEffect } from "react";
import { UserProvider } from "@/providers/UserProvider";
declare global {
  interface Window {
    ReactNativeWebView?: { postMessage: (msg: string) => void };
  }
}

const root = createRoot(document.getElementById("app")!);

function Root() {
  const params = new URLSearchParams(window.location.search);
  const urlToken = params.get("token") ?? undefined;
  const loadConversationId = params.get("conversationId") ?? undefined;
  const isMobile = !!urlToken;

  const [showApp, setShowApp] = React.useState(false);
  const { user } = useAuth0();
  const [authToken, setAuthToken] = React.useState<string | undefined>(
    urlToken
  );
  const [mobileEmail, setMobileEmail] = React.useState<string | undefined>(
    undefined
  );
  const [mobileName, setMobileName] = React.useState<string | undefined>(
    undefined
  );
  const [mobileAuthError, setMobileAuthError] = React.useState(false);
  const randomId = useRef<string | null>(
    isMobile ? Math.random().toString(36).slice(2) : crypto.randomUUID()
  );

  const resolvedUser =
    mobileEmail ?? (user?.email ? encodeURIComponent(user.email) : undefined);
  const resolvedName = mobileName ?? user?.name;

  useEffect(() => {
    if (!urlToken) {
      return;
    }
    fetch(`https://${import.meta.env.VITE_AUTH0_DOMAIN}/userinfo`, {
      headers: { Authorization: `Bearer ${urlToken}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Auth0 returned ${res.status}`);
        return res.json() as Promise<{ email?: string; name?: string }>;
      })
      .then((info) => {
        if (info.email) {
          setMobileEmail(encodeURIComponent(info.email));
          setMobileName(info.name);
          setShowApp(true);
        } else {
          setMobileAuthError(true);
        }
      })
      .catch((err) => {
        console.error("Mobile token validation failed:", err);
        setMobileAuthError(true);
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({ type: "SESSION_EXPIRED" })
        );
      });
  }, []);

  return showApp === true ? (
    <ErrorBoundary>
      <UserProvider
        value={{
          userEmail: resolvedUser,
          userName: resolvedName,
          authToken,
          isMobile,
          isAuthenticated: true,
        }}
      >
        <App randomId={randomId.current} authToken={authToken} loadConversationId={loadConversationId} />
      </UserProvider>
    </ErrorBoundary>
  ) : isMobile && mobileAuthError ? (
    <div className="flex flex-col items-center justify-center h-screen">
      <div className="text-center space-y-2 p-6">
        <p className="font-semibold">Session expired</p>
        <p className="text-sm text-neutral-500">Please logout and try again.</p>
      </div>
    </div>
  ) : isMobile ? (
    // Show a loading state while validating the mobile token
    <div className="flex items-center justify-center h-screen">Loading...</div>
  ) : (
    <LoginScreen
      onContinue={() => setShowApp(true)}
      setAuthToken={setAuthToken}
    />
  );
}

root.render(
  <Providers>
    <Root />
  </Providers>
);
